<template lang="pug">
y-dialog(v-if="showDialog" v-model="showDialog" persistent scrollable content-class="round-2xl")
  v-card
    v-card-title.pa-4
      v-row(no-gutters)
        v-col(cols="10" offset="1")
          .text-xl.font-bold.text-center Add treatment in bulk
        v-col(cols="1" align="right")
          v-btn(icon @click="showDialog = false" small color="primary")
            v-icon mdi-close
    v-divider
    v-card-text.pa-6
      v-row(no-gutters)
        v-col.pr-4(cols="8")
          template(v-if="isFetching")
            v-skeleton-loader(type="list-item-three-line@5")
          template(v-else)
            div.text-sm.neutral700--text Tick the checkbox to select item.
            v-row.mt-2(no-gutters)
              v-col(cols="8")
                v-text-field.text-sm.round-xl.mt-2(v-model="itemName" dense placeholder="Type in item name, uom or group name" outlined hide-details="auto" clearable append-icon="mdi-magnify" @input="searchStart")
                  template(v-slot:append)
                    v-icon.small.mr-2 mdi-magnify
            v-data-table.mt-4(v-model="selected" :headers="headers" :items="products" item-key="id" :items-per-page="pageSize" :loading="isFetchingProducts" fixed-header :height="treatmentTableHeight" disable-sort hide-default-footer loader-height="2" show-select)
              template(v-slot:header.data-table-select="{ props }")
                yc-checkbox(:input-value="allSelected" :indeterminate="props.indeterminate" :disabled="products.length === 0 || allDisabled" @change="handleSelectAll")
              template(v-slot:item.data-table-select="{ item, select, isSelected }")
                yc-checkbox(:input-value="isSelected" :disabled="item.disabled" @change="select($event)")
              template(v-slot:item.name="{ item }")
                .py-4
                  .d-flex.align-center.justify-space-between
                    .text-xs.font-medium(:class="[{'neutral500--text': item.hasError},{'danger500--text': item.inventory_enabled && item.available <= 0}]") {{ item.name }}
                    yz-badge(v-if="item.branch_for" x-small color="blue-100" text-color="blue-500" border) HQ
                  .text-xxs.danger500--text(v-show="item.hasError") {{ item.errorMessage }}
              template(v-slot:item.available="{ item }")
                .text-xs.font-medium(:class="[{'danger500--text': item.available <= 0}]") {{ item.available }}
              template(v-slot:item.stocks="{ item }")
                .text-xs.font-medium(:class="[{'danger500--text': item.stocks <= 0}]") {{ item.stocks }}
              template(v-slot:item.groups="{ item }")
                v-tooltip(top color="black" content-class="black tooltip-top-arrow")
                  template(#activator="{ on, attrs }")
                    div(v-bind="attrs" v-on="on" style="max-width: 150px;")
                      .text-xs.font-medium.neutral700--text.cursor-help.truncate {{ item.groups }}
                  div(style="max-width: 500px;")
                    .text-xs.font-medium {{ item.groups }}
              template(v-slot:item.price_range="{ item }")
                template(v-for="(price, idx) in item.price_range")
                  span.text-xs.font-medium(:key="`pr-${idx}`") {{ price | displayPrice($currency) }}
                    span.mx-1(v-if="item.price_range.length > 1 && !idx") -
            div.d-flex.justify-end.mt-2(v-if="totalCount > pageSize")
              yc-pagination-custom(v-model="currentPage" :parentPageSize.sync="pageSize" :count="totalCount" attach menu-top @input="pageChanged")
        v-col
          v-card.border.rounded-lg.pa-6.height-adjustment(outlined :style="{ '--selected-item-height': selectedItemHeight }")
            v-row(no-gutters)
              v-col
                .text-sm.font-semibold Selected item
              v-spacer
              v-col.d-flex.justify-end(v-if="selected.length > 0")
                v-btn.button-xs.neutral700--text.underline.mr-1(text @click="handleRemoveAllItem") Remove all
            .selected-items-container.mt-2
              div(v-if="selected.length === 0")
                v-card.px-4.py-8.mt-8.border-dashed(outlined)
                  .d-flex.flex-column.align-center.justify-center.text-center
                    v-icon.mb-6(size="30") $yzi-colors
                    .text-md.font-semibold.mb-1 No items selected
                    .text-sm Any selected items from your list will show up here.
              template(v-else)
                div(v-for="(selectedItem, selectedItemIdx) in selected" :key="selectedItemIdx")
                  v-row.pa-2.align-center(no-gutters)
                    v-col(cols="2")
                      .text-sm {{ selectedItemIdx + 1 }}
                    v-col.mx-2
                      .text-sm {{ selectedItem.name }}
                    v-col.d-flex.justify-end(cols="2")
                      v-btn.button-xs.danger700--text.px-0.underline(text @click="handleRemoveItem(selectedItem)") Remove
                  v-divider(v-if="selectedItemIdx !== selected.length - 1")

    v-divider
    v-card-actions.px-6.py-2
      v-spacer
      v-btn.button-md(:loading="isLoading" color="primary" depressed :disabled="selected.length === 0" @click="handleInsertToTreatmentPlan") Insert to treatment plan

</template>

<script>
import { debounce } from '@/utils'
import { mapGetters } from 'vuex'

export default {
  props: {
    open: {
      type: Boolean
    },
    isOTC: {
      type: Boolean
    },
    inventoryOnly: {
      type: Boolean,
      default: false
    },
    allowOutOfStock: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      isFetching: false,
      isFetchingProducts: false,
      isLoading: false,
      totalCount: 0,
      pageSize: 20,
      currentPage: 1,
      itemName: null,
      products: [],
      selected: [],
      headers: [
        { text: 'Name', value: 'name' },
        { text: 'Available', value: 'available' },
        { text: 'Stock', value: 'stocks'},
        { text: 'UOM', value: 'uom' },
        { text: 'Group', value: 'groups' },
        { text: 'Price', value: 'price_range' },
      ],
    }
  },
  mounted () {
    this.fetchProducts(true)
  },
  watch: {},
  computed: {
    ...mapGetters({
      company: 'getCompany',
      hqSelectedCompany: 'hq/getSelectedCompany',
      isHq: 'hq/getIsHq'
    }),
    showDialog: {
      get() {
        return this.open
      },
      set(newValue) {
        this.$emit('update:open', newValue)
      }
    },
    allDisabled () {
      return this.products.every(item => item.disabled)
    },
    allSelected () {
      if (this.allDisabled) {
        return false
      }
      const productIds = this.products.filter(item => !item.disabled).map(item => item.id)
      return this.selected.filter(item => productIds.includes(item.id)).length === productIds.length
    },
    screenHeight () {
      return window.innerHeight
    },
    treatmentTableHeight () {
      return this.screenHeight - 395
    },
    selectedItemHeight () {
      return this.screenHeight - 258 + 'px'
    }
  },
  methods: {
    pageChanged () {
      this.fetchProducts()
    },
    searchStart: debounce(function () {
      this.currentPage = 1
      this.fetchProducts()
    }, 750),
    async fetchProducts (firstLoad = false) {
      try {
        if (firstLoad) {
          this.isFetching = true
        }
        this.isFetchingProducts = true
        let params = {
          page: this.currentPage,
          page_size: this.pageSize,
          search: this.itemName || null,
          status: 'published'
        }
        if (this.inventoryOnly) {
          params.relations = 'medication'
        }
        const companySlug = this.isHq ? this.hqSelectedCompany.slug : this.company.slug
        const { data } = await this.$axios.get(`/clinics/${companySlug}/treatment_products`, { params })
        if (data.count > 0) {
          this.products = data.results.map(result => this.sanitizeData(result))
        } else {
          this.products = []
        }
        this.totalCount = data.count
      } catch (error) {
        this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
      } finally {
        this.isFetching = false
        this.isFetchingProducts = false
      }
    },
    hasStock(item) {
      if (!item.inventory_enabled) {
        return true
      }
      if (item.available_qty > 0) {
        return true
      }
      return false
    },
    getAvailable (item) {
      if (!item.inventory_enabled) {
        return null
      }
      return item.available_qty
    },
    getStock (item) {
      if (!item.inventory_enabled) {
        return null
      }
      return item.inventory_qty
    },
    hasError (item) {
      return item.type === 'documents' || this.getAvailable(item) === 0
    },
    getErrorMessage (item) {
      if (this.hasError(item)) {
        if (item.type === 'documents') return 'Medical document cannot be added for multiple bulk. Please use single add.'
        if (this.getAvailable(item) === 0) return 'Out of stock'
      }
      return null
    },
    isDisabled (item) {
      if (this.allowOutOfStock) return item.type === 'documents'
      return !this.hasStock(item) || this.hasError(item)
    },
    sanitizeData (item) {
      const groups = item.groups.join(', ')
      const disabled = this.isDisabled(item)
      const hasError = this.allowOutOfStock ? (item.type === 'documents') : this.hasError(item)
      return {
        ...item,
        groups,
        available: this.getAvailable(item),
        stocks: this.getStock(item),
        disabled,
        hasError,
        errorMessage: hasError ? this.getErrorMessage(item) : null,
      }
    },
    handleSelectAll (value) {
      if (value) {
        this.selected = [...this.selected, ...this.products.filter(item => !this.isDisabled(item))]
      } else {
        const productsId = this.products.map(item => item.id)
        this.selected = this.selected.filter(item => !productsId.includes(item.id))
      }
    },
    handleRemoveItem (item) {
      this.selected = this.selected.filter(selectedItem => selectedItem.id !== item.id)
    },
    handleRemoveAllItem () {
      this.selected = []
    },
    handleInsertToTreatmentPlan () {
      this.isLoading = true
      this.$emit('insert', this.selected)
    }
  }
};
</script>

<style lang="scss" scoped>
.border {
  border: 1px solid var(--v-neutral300) !important;
}
.height-adjustment {
  height: var(--selected-item-height);
}
.selected-items-container {
  height: calc(100% - 40px);
  overflow-y: auto;
}
::v-deep {
  .v-text-field--outlined {
    fieldset {
      border-color: var(--v-neutral400);
    }
  }
  .error--text {
    color: var(--v-danger500) !important;
    fieldset {
      border-color: var(--v-danger500);
    }
  }
  .combostyle .v-input__icon {
    min-width: 22px;
    width: 22px;
    margin-top: 4px;
  }
}
</style>